import { FC, useCallback, CSSProperties, useEffect, useState, MouseEvent, useRef } from "react";

import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { Theme } from "@mui/material/styles/createTheme";

interface HighlightProps {
  anchorEl: HTMLElement | null;
  children: React.ReactNode;
  onClose: () => void;
  open: boolean;
  position: "top" | "bottom" | "left";
  fullWidth?: boolean;
  offset?: {
    top?: {
      value?: number;
      includeChildHeight?: boolean;
    };
  };
}

const BACKDROP_Z_INDEX = 10000;
const transitionStyles: CSSProperties = {
  transition: "all 100ms ease-out",
  transitionProperty: "opacity",
};

const limitPosition = (theme: Theme, axisMax: number, position: number): number => {
  const minPosition = theme.legacySpacing(2);
  if (position < minPosition) {
    return minPosition;
  }
  const maxPosition = axisMax - theme.legacySpacing(2);
  if (position > maxPosition) {
    return maxPosition;
  }
  return position;
};

export const DashboardHighlight: FC<HighlightProps> = ({
  anchorEl,
  children,
  onClose: externalOnClose,
  open: externalOpen,
  position,
  offset,
}) => {
  const theme = useTheme();

  const childRef = useRef<HTMLDivElement>(null);

  const [showParent, setShowParent] = useState(false);
  const [showChild, setShowChild] = useState(false);
  const [anchorBoundingRect, setAnchorBoundingRect] = useState(anchorEl?.getBoundingClientRect());
  const [bodyBoundingRect, setBodyBoundingRect] = useState(document.body.getBoundingClientRect());

  const open = Boolean(externalOpen && anchorBoundingRect && bodyBoundingRect);

  const updateBoundingRect = useCallback(() => {
    setAnchorBoundingRect(anchorEl?.getBoundingClientRect());
    setBodyBoundingRect(document.body.getBoundingClientRect());
  }, [anchorEl]);

  useEffect(() => {
    setShowParent(open);
    if (open) {
      // Function to update bounding rect and show highlight
      const updateAndShowHighlight = () => {
        updateBoundingRect();
        setTimeout(() => setShowChild(true), 100);
      };
      // We need to update the bounding rect and show the highlight on scroll
      const handleScroll = () => {
        let timer: NodeJS.Timeout | null = null;
        if (timer !== null) {
          clearTimeout(timer);
        }
        // We need to wait for the scroll to finish
        timer = setTimeout(function () {
          updateAndShowHighlight();
        }, 150);
      };
      // We bind the function to the scroll event
      window.addEventListener("scroll", handleScroll, false);

      if (anchorEl) {
        // We scroll to the anchor element to account for the case where the element is not visible
        anchorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      // Regardless of the scroll event, we will show the highlight after 500ms
      const timeout = setTimeout(() => {
        updateAndShowHighlight();
      }, 500);

      return () => {
        // Clean up timer and scroll event listener
        clearTimeout(timeout);
        window.removeEventListener("scroll", handleScroll);
      };
    }
    setShowChild(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    window.addEventListener("resize", updateBoundingRect);
    window.addEventListener("scroll", updateBoundingRect);
    updateBoundingRect();
    return () => {
      window.removeEventListener("resize", updateBoundingRect);
      window.removeEventListener("scroll", updateBoundingRect);
    };
  }, [anchorEl, updateBoundingRect]);

  const childSx = useCallback(
    (theme: Theme) => {
      const childBoundingRect = childRef.current?.getBoundingClientRect();
      if (!anchorEl || !anchorBoundingRect || !bodyBoundingRect || !childBoundingRect) {
        return {};
      }

      const commonStyles: CSSProperties = {
        position: "fixed",
        zIndex: BACKDROP_Z_INDEX + 1,
        opacity: showChild ? 1 : 0,
        ...transitionStyles,
      };

      if (position === "top") {
        return {
          ...commonStyles,
          top: limitPosition(
            theme,
            bodyBoundingRect.height,
            anchorBoundingRect.top -
              childBoundingRect.height -
              theme.legacySpacing(2) +
              (offset?.top?.value || 0) +
              (offset?.top?.includeChildHeight ? childBoundingRect.height : 0)
          ),
          left: limitPosition(
            theme,
            bodyBoundingRect.width,
            anchorBoundingRect.left -
              childBoundingRect.width / 2 +
              anchorBoundingRect.width / 2 +
              theme.legacySpacing(1)
          ),
          paddingRight: theme.spacing(2),
          paddingTop: theme.spacing(2),
        };
      }

      if (position === "bottom") {
        return {
          ...commonStyles,
          top: limitPosition(theme, bodyBoundingRect.height, anchorBoundingRect.bottom + theme.legacySpacing(2)),
          left: limitPosition(
            theme,
            bodyBoundingRect.width,
            anchorBoundingRect.left -
              childBoundingRect.width / 2 +
              anchorBoundingRect.width / 2 +
              theme.legacySpacing(1)
          ),
          paddingRight: theme.spacing(2),
          paddingBottom: theme.spacing(2),
        };
      }

      return {
        ...commonStyles,
        top: limitPosition(
          theme,
          bodyBoundingRect.height,
          anchorBoundingRect.top + anchorBoundingRect.height / 2 - childBoundingRect.height / 2 + theme.legacySpacing(1)
        ),
        right: limitPosition(
          theme,
          bodyBoundingRect.width,
          bodyBoundingRect.width - anchorBoundingRect.left + theme.legacySpacing(2)
        ),
        paddingLeft: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      };
    },
    [
      anchorEl,
      anchorBoundingRect,
      bodyBoundingRect,
      showChild,
      position,
      offset?.top?.value,
      offset?.top?.includeChildHeight,
    ]
  );

  const onClose = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      externalOnClose();
    },
    [externalOnClose]
  );

  const onChildClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  if (!anchorEl) {
    return null;
  }

  return (
    <>
      <Backdrop
        open={open}
        style={{ opacity: 0, pointerEvents: "painted", zIndex: BACKDROP_Z_INDEX }}
        onClick={onClose}
      />
      <div
        style={{
          display: open ? "block" : "none",
          position: "fixed",
          top: anchorBoundingRect?.top,
          left: anchorBoundingRect?.left,
          width: anchorBoundingRect?.width,
          height: anchorBoundingRect?.height,
          zIndex: BACKDROP_Z_INDEX + 1,
          borderRadius: theme.radius(1),
          boxShadow: "0 0 0 200vmax rgba(0,0,0,.3)",
          opacity: showParent ? 1 : 0,
          overflow: "hidden",
          ...transitionStyles,
        }}
        onClick={onClose}
      >
        <Box sx={childSx} onClick={onChildClick} ref={childRef}>
          {children}
        </Box>
      </div>
    </>
  );
};
